import {
  advanceField,
  createAsteroidField,
  ARENA_HEIGHT,
  type AsteroidField,
} from './asteroid-field';
import type { CharacterId } from './characters';
import type { GameMode } from './player-select';
import {
  advanceBody,
  createBody,
  firstCollision,
  hitRadiusFor,
  isNearMiss,
  NO_INPUT,
  resolveBump,
  type Body,
  type MoveIntent,
} from './racer-movement';
import { randomSeed } from './rng';

/**
 * Race lifecycle:
 *
 *   get_ready -> countdown (3, 2, 1, GO!) -> racing -> finished
 *
 * A wipe-out no longer ends the round. The survivor keeps flying and keeps
 * scoring, chasing the number the loser already posted; the round ends when
 * everybody is out. That is what makes the two scores differ, and what gives
 * the last racer alive something to play for.
 */
export type RacePhase = 'get_ready' | 'countdown' | 'racing' | 'finished';

export const GET_READY_MS = 1_500;
/** 3, 2, 1, GO! — four beats of 750ms. */
export const COUNTDOWN_STEP_MS = 750;
export const COUNTDOWN_STEPS = 4;
export const COUNTDOWN_MS = COUNTDOWN_STEP_MS * COUNTDOWN_STEPS;

/** Score awarded per second survived. */
export const SCORE_PER_SECOND = 100;
/** Score awarded for each rock shaved past. Cheap individually, adds up. */
export const SCORE_PER_NEAR_MISS = 25;

export const RACER_START_X = 220;

export interface Racer {
  player: 1 | 2;
  character: CharacterId;
  alive: boolean;
  /** Milliseconds this racer survived once the race began. */
  survivedMs: number;
  /** Rocks shaved past without being hit. */
  nearMisses: number;
  body: Body;
}

/** Per-player input for a single step, keyed by player number. */
export type RaceInput = Partial<Record<1 | 2, MoveIntent>>;

/** Something worth a sound or a shake, surfaced for the scene to dress up. */
export interface RaceEvents {
  /** Players wiped out during this step. */
  wipeouts: (1 | 2)[];
  /** Players who shaved past a rock during this step. */
  nearMisses: (1 | 2)[];
  /** True when two racers collided hard enough to matter. */
  bumped: boolean;
}

export const NO_EVENTS: RaceEvents = { wipeouts: [], nearMisses: [], bumped: false };

export interface RaceState {
  mode: GameMode;
  phase: RacePhase;
  /** Milliseconds spent in the current phase. */
  phaseElapsedMs: number;
  /** Milliseconds since "GO!". Zero until racing starts. */
  raceElapsedMs: number;
  racers: Racer[];
  /**
   * One field, one arena, both racers.
   *
   * A networked match can reproduce the whole field from `field.seed` alone,
   * so only input and deaths need to cross the wire.
   */
  field: AsteroidField;
  /** What just happened, for the scene to react to. Not part of the rules. */
  events: RaceEvents;
}

export function createRaceState(
  mode: GameMode,
  selections: { 1: CharacterId | null; 2: CharacterId | null },
  seed: number = randomSeed(),
): RaceState {
  const racers: Racer[] = [];
  const spawn = (player: 1 | 2, character: CharacterId, y: number): Racer => ({
    player,
    character,
    alive: true,
    survivedMs: 0,
    nearMisses: 0,
    body: createBody(RACER_START_X, y),
  });

  if (selections[1]) {
    racers.push(
      spawn(1, selections[1], mode === 'local_multiplayer' ? ARENA_HEIGHT / 3 : ARENA_HEIGHT / 2),
    );
  }
  if (mode === 'local_multiplayer' && selections[2]) {
    racers.push(spawn(2, selections[2], (ARENA_HEIGHT * 2) / 3));
  }

  if (racers.length === 0) {
    throw new Error('Cannot start a race with no racers selected');
  }

  return {
    mode,
    phase: 'get_ready',
    phaseElapsedMs: 0,
    raceElapsedMs: 0,
    racers,
    field: createAsteroidField(seed),
    events: NO_EVENTS,
  };
}

/**
 * Advances the race by `deltaMs`. Pure: returns a new state, so the whole
 * lifecycle is testable without a render loop.
 *
 * A single call may cross several phase boundaries — a backgrounded tab can
 * hand us a delta longer than the entire countdown — so leftover time cascades
 * forward instead of being clamped or dropped.
 */
export function advanceRace(
  state: RaceState,
  deltaMs: number,
  input: RaceInput = {},
): RaceState {
  if (deltaMs < 0) {
    throw new Error(`advanceRace expects a non-negative delta, received: ${deltaMs}`);
  }
  if (state.phase === 'finished' || deltaMs === 0) {
    return state;
  }

  let current = state;
  let remaining = deltaMs;

  while (remaining > 0) {
    const budget = phaseDurationMs(current.phase);

    // The racing phase is open-ended: absorb the rest and stop.
    if (budget === null) {
      return stepRacing(current, remaining, input);
    }

    const phaseElapsedMs = current.phaseElapsedMs + remaining;
    if (phaseElapsedMs < budget) {
      return { ...current, phaseElapsedMs, events: NO_EVENTS };
    }

    // Phase complete — roll the surplus into the next one.
    remaining = phaseElapsedMs - budget;
    current =
      current.phase === 'get_ready'
        ? { ...current, phase: 'countdown', phaseElapsedMs: 0 }
        : { ...current, phase: 'racing', phaseElapsedMs: 0, raceElapsedMs: 0 };
  }

  return current;
}

/**
 * One step of live racing: fly each racer, resolve any body-check between
 * them, advance the field, then score near misses and test for impacts.
 *
 * Movement happens before collision so a racer cannot be killed by a rock they
 * had already steered clear of within the same step.
 */
function stepRacing(state: RaceState, deltaMs: number, input: RaceInput): RaceState {
  const raceElapsedMs = state.raceElapsedMs + deltaMs;
  const previousRocks = state.field.asteroids;
  const field = advanceField(state.field, deltaMs);

  let racers = state.racers.map((racer) =>
    racer.alive
      ? {
          ...racer,
          body: advanceBody(
            racer.body,
            input[racer.player] ?? NO_INPUT,
            deltaMs,
            racer.character,
          ),
          survivedMs: raceElapsedMs,
        }
      : racer,
  );

  // Body-check. Only living racers can shove each other.
  let bumped = false;
  const [first, second] = racers;
  if (first?.alive && second?.alive) {
    const [a, b] = resolveBump(
      first.body,
      second.body,
      hitRadiusFor(first.character),
      hitRadiusFor(second.character),
    );
    bumped = a !== first.body || b !== second.body;
    racers = [
      { ...first, body: a },
      { ...second, body: b },
    ];
  }

  const wipeouts: (1 | 2)[] = [];
  const nearMisses: (1 | 2)[] = [];

  racers = racers.map((racer) => {
    if (!racer.alive) {
      return racer;
    }
    const radius = hitRadiusFor(racer.character);
    const { x, y } = racer.body;

    if (firstCollision(x, y, radius, field.asteroids) !== null) {
      wipeouts.push(racer.player);
      return { ...racer, alive: false };
    }

    // A near miss is counted once, as the rock crosses behind the racer, so a
    // rock lingering alongside cannot be farmed for points.
    const shaved = countCrossings(previousRocks, field, x, y, radius);
    if (shaved > 0) {
      nearMisses.push(racer.player);
      return { ...racer, nearMisses: racer.nearMisses + shaved };
    }
    return racer;
  });

  // The round is over only once nobody is left flying.
  const everyoneOut = racers.every((racer) => !racer.alive);

  return {
    ...state,
    phase: everyoneOut ? 'finished' : 'racing',
    phaseElapsedMs: everyoneOut ? 0 : state.phaseElapsedMs + deltaMs,
    raceElapsedMs,
    racers,
    field,
    events: { wipeouts, nearMisses, bumped },
  };
}

/** Rocks that passed the racer during this step, close but not close enough. */
function countCrossings(
  before: readonly { id: number; x: number }[],
  field: AsteroidField,
  x: number,
  y: number,
  radius: number,
): number {
  let count = 0;
  for (const asteroid of field.asteroids) {
    const previous = before.find((rock) => rock.id === asteroid.id);
    if (!previous) {
      continue;
    }
    const crossed = previous.x >= x && asteroid.x < x;
    if (crossed && isNearMiss(x, y, asteroid, radius)) {
      count += 1;
    }
  }
  return count;
}

/** Milliseconds a phase lasts, or null if it runs until everybody is out. */
function phaseDurationMs(phase: RacePhase): number | null {
  switch (phase) {
    case 'get_ready':
      return GET_READY_MS;
    case 'countdown':
      return COUNTDOWN_MS;
    default:
      return null;
  }
}

/** The number or word currently on screen during the countdown. */
export function countdownLabel(state: RaceState): string | null {
  if (state.phase !== 'countdown') {
    return null;
  }
  const step = Math.floor(state.phaseElapsedMs / COUNTDOWN_STEP_MS);
  return ['3', '2', '1', 'GO!'][Math.min(step, COUNTDOWN_STEPS - 1)]!;
}

/**
 * Knocks one or more racers out. The round ends only when nobody is left.
 *
 * Several players can be passed at once because two racers can be caught in
 * the same frame.
 */
export function killRacer(state: RaceState, ...players: (1 | 2)[]): RaceState {
  if (state.phase !== 'racing' || players.length === 0) {
    return state;
  }

  const racers = state.racers.map((racer) =>
    players.includes(racer.player) ? { ...racer, alive: false } : racer,
  );
  const everyoneOut = racers.every((racer) => !racer.alive);

  return {
    ...state,
    phase: everyoneOut ? 'finished' : 'racing',
    phaseElapsedMs: everyoneOut ? 0 : state.phaseElapsedMs,
    racers,
    events: { ...NO_EVENTS, wipeouts: players },
  };
}

export function scoreFor(racer: Racer): number {
  return (
    Math.floor((racer.survivedMs / 1000) * SCORE_PER_SECOND) +
    racer.nearMisses * SCORE_PER_NEAR_MISS
  );
}

/**
 * The highest score once the round is over.
 *
 * Now that the survivor plays on, scores genuinely differ, so this is decided
 * on points rather than on who happened to still be breathing. Returns null in
 * single player, and on an exact tie.
 */
export function winner(state: RaceState): Racer | null {
  if (state.phase !== 'finished' || state.racers.length < 2) {
    return null;
  }
  const [first, second] = [...state.racers].sort((a, b) => scoreFor(b) - scoreFor(a));
  if (!first || !second || scoreFor(first) === scoreFor(second)) {
    return null;
  }
  return first;
}
